import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Badge, ButtonGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faUser,
  faUsers,
  faInfoCircle,
  faBuilding,
  faCog,
  faFilter,
  faClock,
  faCheckCircle,
  faCalendarDay,
} from "@fortawesome/free-solid-svg-icons";
import CadastroMetaOffCanvas from "./CadastroMetaOffCanvas";
import { Target, UsuarioMeta } from "./types";
import { MetaResumida, MetaRequest } from "../../../types/meta";
import DeleteRestoreModal from "../../../components/DeleteRestoreModal";
import {
  useMetas,
  useExcluirMeta,
  useCriarMeta,
  useAtualizarMeta,
  useContagemMetasPorStatus,
} from "../../../hooks/useMeta";
import { useToast } from "../../../providers/ToastProvider";

interface TargetManagementProps {
  targets: Target[];
  onTargetsChange: (targets: Target[]) => void;
  isActive?: boolean;
}

export const TargetManagement: React.FC<TargetManagementProps> = ({
  targets,
  onTargetsChange,
  isActive = true,
}) => {
  const [showTargetOffCanvas, setShowTargetOffCanvas] = useState(false);
  const [editingTarget, setEditingTarget] = useState<Target | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("active");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState<MetaResumida | null>(
    null
  );
  const { showToast } = useToast();

  // Integração com API
  const {
    data: metasResult,
    isLoading,
    error,
    refetch,
  } = useMetas(
    {
      status: activeFilter === "" ? undefined : activeFilter,
    },
    { enabled: isActive }
  );
  
  // Hook para contagem de metas por status
  const { data: filterCounts = {} } = useContagemMetasPorStatus();
  
  const excluirMetaMutation = useExcluirMeta();
  const criarMetaMutation = useCriarMeta();
  const atualizarMetaMutation = useAtualizarMeta();

  const metasFromAPI = metasResult?.data || [];

  // Mostrar toast quando houver erro no carregamento
  useEffect(() => {
    if (error) {
      showToast("Erro ao carregar metas. Tente novamente.", "error");
    }
  }, [error, showToast]);

  const handleOffCanvasSave = (metaData: MetaRequest) => {
    if (editingTarget) {
      // Editar meta existente
      atualizarMetaMutation.mutate(
        { id: editingTarget.id, meta: metaData },
        {
          onSuccess: (response) => {
            if (response.isSuccess) {
              showToast("Meta atualizada com sucesso!", "success");
              setShowTargetOffCanvas(false);
              refetch();
            } else {
              showToast(response.message || "Erro ao atualizar meta", "error");
            }
          },
          onError: (error: any) => {
            showToast(
              error.response?.data?.message || "Erro ao atualizar meta",
              "error"
            );
          },
        }
      );
    } else {
      // Criar nova meta
      criarMetaMutation.mutate(metaData, {
        onSuccess: (response) => {
          if (response.isSuccess) {
            showToast("Meta criada com sucesso!", "success");
            setShowTargetOffCanvas(false);
            refetch();
          } else {
            showToast(response.message || "Erro ao criar meta", "error");
          }
        },
        onError: (error: any) => {
          showToast(
            error.response?.data?.message || "Erro ao criar meta",
            "error"
          );
        },
      });
    }
  };

  const handleAddTarget = () => {
    setEditingTarget(null);
    setShowTargetOffCanvas(true);
  };

  const handleEditTarget = (target: Target) => {
    setEditingTarget(target);
    setShowTargetOffCanvas(true);
  };

  const handleDeleteTarget = (targetId: string) => {
    const meta = metasFromAPI.find((m) => m.id.toString() === targetId);

    if (meta) {
      setTargetToDelete(meta);
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = () => {
    if (targetToDelete) {
      excluirMetaMutation.mutate(targetToDelete.id, {
        onSuccess: (response) => {
          if (response.isSuccess) {
            showToast("Meta excluída com sucesso!", "success");
            setShowDeleteModal(false);
            setTargetToDelete(null);
            refetch();
          } else {
            showToast(response.message || "Erro ao excluir meta", "error");
          }
        },
        onError: (error: any) => {
          showToast(
            error.response?.data?.message || "Erro ao excluir meta",
            "error"
          );
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTargetToDelete(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    // Parse manual para evitar problemas de timezone
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month - 1 porque Date usa 0-based months
    return date.toLocaleDateString("pt-BR");
  };

  const getFilterCount = (filterType: string) => {
    return filterCounts[filterType] || 0;
  };

  // Converter MetaResumida para Target (mantém compatibilidade com design existente)
  const convertMetaToTarget = (meta: MetaResumida): Target => {
    const firstUser = meta.usuarios[0];
    const isMultipleUsers = meta.usuarios.length > 1;

    return {
      id: meta.id.toString(),
      employeeId: isMultipleUsers
        ? "multiple"
        : firstUser?.usuarioId.toString() || "0",
      employeeName: isMultipleUsers
        ? `${meta.usuarios.length} colaboradores`
        : firstUser?.nomeUsuario || "Usuário não encontrado",
      period: "custom" as const,
      targetValue: meta.valorMeta, // Soma total de todos os usuários
      startDate: meta.dataInicio ? meta.dataInicio.split("T")[0] : "",
      endDate: meta.dataFim ? meta.dataFim.split("T")[0] : "",
      status: "active",
      createdAt: new Date().toISOString().split("T")[0],
      imageUrl: firstUser?.imageUrl,
      description: meta.descricao,
      usuarios: meta.usuarios.map((u) => ({
        usuarioId: u.usuarioId,
        nomeUsuario: u.nomeUsuario,      
        valorMeta: u.valorMeta,
        imageUrl: u.imageUrl,
      })),
    };
  };

  const MetaCard = ({ meta }: { meta: MetaResumida }) => {
    const target = convertMetaToTarget(meta);
    const [showAllUsers, setShowAllUsers] = useState(false);

    const displayedUsers = showAllUsers
      ? meta.usuarios
      : meta.usuarios.slice(0, 2);
    const hasMoreUsers = meta.usuarios.length > 2;

    return (
      <Col md={6} lg={4} className="mb-4" key={meta.id}>
        <Card className="h-100 shadow-sm border-0">
          <Card.Body className="d-flex flex-column">
            {/* Header com avatar/ícone e informações básicas */}
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style={{
                  width: "48px",
                  height: "48px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <FontAwesomeIcon icon={faUsers} size="lg" />
              </div>

              <div className="flex-grow-1 min-width-0">
                <h5 className="mb-1 fw-semibold text-truncate">
                  {target.description || "Meta sem descrição"}
                </h5>              
                <div className="text-muted small">
                  {meta.usuarios.length} {meta.usuarios.length === 1 ? "vendedor" : "vendedores"}
                </div>
              </div>
            </div>

            {/* Informações da meta */}
            <div className="mb-3">             
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted small">Valor Total:</span>
                <span className="fw-bold text-primary">
                  {formatCurrency(target.targetValue)}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">Período:</span>
                <span className="small">
                  {formatDate(target.startDate)} - {formatDate(target.endDate)}
                </span>
              </div>
            </div>

            {/* Detalhes dos usuários */}
            <div className="mb-3">
              <small className="text-muted fw-semibold">Colaboradores:</small>
              <div className="mt-2">
                {displayedUsers.map((usuario, index) => (
                  <div
                    key={usuario.usuarioId}
                    className="d-flex align-items-center justify-content-between mb-2"
                  >
                    <div className="d-flex align-items-center flex-grow-1 me-2">
                      {usuario.imageUrl ? (
                        <img
                          src={usuario.imageUrl}
                          alt={usuario.nomeUsuario}
                          className="rounded-circle me-2"
                          style={{
                            width: "24px",
                            height: "24px",
                            objectFit: "cover",
                            border: "1px solid #e9ecef",
                          }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-2"
                          style={{
                            width: "24px",
                            height: "24px",
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                            fontSize: "10px",
                          }}
                        >
                          <FontAwesomeIcon icon={faUser} />
                        </div>
                      )}
                      <small className="text-truncate">
                        {usuario.nomeUsuario}
                      </small>
                    </div>
                    <small className="text-primary fw-semibold">
                      {formatCurrency(usuario.valorMeta)}
                    </small>
                  </div>
                ))}
                {hasMoreUsers && (
                  <button
                    className="btn btn-link btn-sm p-0 text-decoration-none"
                    onClick={() => setShowAllUsers(!showAllUsers)}
                    style={{ fontSize: "12px" }}
                  >
                    {showAllUsers
                      ? "Mostrar menos"
                      : `+${meta.usuarios.length - 2} outros colaboradores`}
                  </button>
                )}
              </div>
            </div>

            {/* Botões de ação */}
            <div className="d-flex gap-2 mt-auto">
              <Button
                variant="outline-primary"
                size="sm"
                className="flex-fill"
                onClick={() => handleEditTarget(target)}               
                disabled={new Date(target.endDate) < new Date()}
              >
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                Editar
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteTarget(target.id)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <>
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header
          className="border-0 py-3"
          style={{
            borderRadius: "16px 16px 0 0",
            borderBottom: "1px solid var(--phoenix-tertiary-bg)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FontAwesomeIcon
                icon={faCog}
                style={{ fontSize: "18px", color: "#0aa3dd" }}
              />
              <div>
                <h6
                  className="mb-1 text-body-emphasis fw-bold"
                  style={{
                    fontSize: "16px",
                    fontFamily: "Inter, -apple-system, sans-serif",
                  }}
                >
                  Gerenciamento de Metas
                </h6>
                <p
                  className="mb-0 text-body-tertiary"
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  Configure e gerencie as metas da equipe de vendas
                </p>
              </div>
            </div>
            <Button variant="primary" size="sm" onClick={handleAddTarget}>
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Nova Meta
            </Button>
          </div>
        </Card.Header>

        <div
          className="px-4 py-4 bg-body-secondary"
          style={{ borderBottom: "1px solid var(--phoenix-tertiary-bg)" }}
        >
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: "40px",
                  height: "40px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
              </div>
              <div>
                <h6 className="mb-0 fw-bold text-body-emphasis">Filtros</h6>
                <small className="text-body-tertiary">
                  Visualize metas por categoria
                </small>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <button
                className={`filter-btn btn ${
                  activeFilter === "active"
                    ? "btn-primary"
                    : "btn-phoenix-secondary"
                }`}
                onClick={() => setActiveFilter("active")}
                style={{
                  borderRadius: "12px",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  border: "none",
                }}
              >
                <FontAwesomeIcon
                  icon={faClock}
                  className="me-2"
                  style={{ fontSize: "12px" }}
                />
                Em Progresso
                <span
                  className={`ms-1 badge ${
                    activeFilter === "active"
                      ? "bg-light text-primary"
                      : "bg-body-tertiary text-body-secondary"
                  }`}
                  style={{
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "700",
                  }}
                >
                  {getFilterCount("active")}
                </span>
              </button>

              <button
                className={`filter-btn btn ${
                  activeFilter === "" ? "btn-info" : "btn-phoenix-secondary"
                }`}
                onClick={() => setActiveFilter("")}
                style={{
                  borderRadius: "12px",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  border: "none",
                }}
              >
                Todos
                <span
                  className={`ms-1 badge ${
                    activeFilter === ""
                      ? "bg-light text-info"
                      : "bg-body-tertiary text-body-secondary"
                  }`}
                  style={{
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "700",
                  }}
                >
                  {getFilterCount("")}
                </span>
              </button>

              <button
                className={`filter-btn btn ${
                  activeFilter === "previous"
                    ? "btn-warning"
                    : "btn-phoenix-secondary"
                }`}
                onClick={() => setActiveFilter("previous")}
                style={{
                  borderRadius: "12px",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  border: "none",
                }}
              >
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="me-2"
                  style={{ fontSize: "12px" }}
                />
                Anteriores
                <span
                  className={`ms-1 badge ${
                    activeFilter === "previous"
                      ? "bg-light text-warning"
                      : "bg-body-tertiary text-body-secondary"
                  }`}
                  style={{
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "700",
                  }}
                >
                  {getFilterCount("previous")}
                </span>
              </button>

              <button
                className={`filter-btn btn ${
                  activeFilter === "future"
                    ? "btn-secondary"
                    : "btn-phoenix-secondary"
                }`}
                onClick={() => setActiveFilter("future")}
                style={{
                  borderRadius: "12px",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  border: "none",
                }}
              >
                <FontAwesomeIcon
                  icon={faCalendarDay}
                  className="me-2"
                  style={{ fontSize: "12px" }}
                />
                Futuras
                <span
                  className={`ms-1 badge ${
                    activeFilter === "future"
                      ? "bg-light text-secondary"
                      : "bg-body-tertiary text-body-secondary"
                  }`}
                  style={{
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "700",
                  }}
                >
                  {getFilterCount("future")}
                </span>
              </button>
            </div>
          </div>
        </div>

        <Card.Body>
          {/* Lista de metas */}
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p className="text-muted mt-2">Carregando metas...</p>
            </div>
          ) : metasFromAPI.length === 0 ? (
            <div className="text-center py-5">
              <FontAwesomeIcon
                icon={faInfoCircle}
                size="3x"
                className="text-muted mb-3"
              />
              <h5 className="text-muted">Nenhuma meta encontrada</h5>
              <p className="text-muted">
                {activeFilter === "active"
                  ? "Não há metas ativas no momento. Crie uma nova meta para começar."
                  : "Não há metas para o filtro selecionado."}
              </p>
              <Button variant="primary" onClick={handleAddTarget}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Criar Meta
              </Button>
            </div>
          ) : (
            <Row>
              {metasFromAPI.map((meta) => (
                <MetaCard key={meta.id} meta={meta} />
              ))}
            </Row>
          )}

          {/* OffCanvas para cadastro/edição */}
          <CadastroMetaOffCanvas
            show={showTargetOffCanvas}
            onHide={() => {
              setShowTargetOffCanvas(false);
              setEditingTarget(null);
            }}
            onSave={handleOffCanvasSave}
            editTarget={editingTarget || undefined}
          />

          {/* Modal de confirmação de exclusão */}
          <DeleteRestoreModal
            show={showDeleteModal}
            isDeleteOperation={true}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            itemName={targetToDelete?.descricao || "Meta sem descrição"}
            itemCode={targetToDelete?.id || ""}
            isLoading={excluirMetaMutation.isPending}
            profileImage={null}
            titleMessage="Confirmar Exclusão"
            confirmMessage="Você está prestes a excluir a meta:"
            infoMessage="Ao excluir esta meta, ela será removida permanentemente do sistema. Todos os dados relacionados, incluindo colaboradores associados e valores, serão perdidos. Esta ação não pode ser desfeita."
            actionButtonText="Excluir Meta"
          />
        </Card.Body>
      </Card>
    </>
  );
};
